import * as React from 'react'

import { Title } from '@factorialco/deprecated-design-system'
import { compact, maxBy, sortBy } from 'lodash'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import SortableBoxList from 'modules/core/components/SortableBoxList'
import type { TrackingProperties } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import { useFeatures } from 'modules/core/hooks/useFeatures'
import i18n from 'modules/core/lib/i18n'
import type { ModalStates } from 'modules/core/lib/questionModalStates'

import type CustomField from 'modules/custom_resources/store/models/customFields/Field'
import type ResourceField from 'modules/custom_resources/store/models/customFields/ResourceField'
import type Field from 'modules/custom_resources/store/models/customResources/Field'

import DeleteModal from './DeleteModal'
import DuplicateModal from './DuplicateModal'
import EditModal from './EditModal'
import Header from './Header'
import { Item } from './Item'
import NewModal from './NewModal'
import { OptionalParamsProcessor } from './OptionalParamsProcessor'

type TitleProps = React.ComponentProps<typeof Title>

interface Props {
  addQuestionLabel?: string
  allowedFieldTypes?: string[]
  allowPermissions?: boolean
  canCreateCustomField?: boolean
  canEditCustomField?: boolean
  canDeleteCustomField?: boolean
  createCustomField: (...args: any[]) => any
  createResourceField?: (...args: any[]) => any
  updateCustomField?: (...args: any[]) => any
  destroyCustomField?: (...args: any[]) => any
  customFields: CustomField[] | Field[]
  description?: string
  descriptionLink?: TitleProps['link']
  emptyMessage?: string
  illustration?: string
  modalButton?: string
  modalButtonUpdate?: string
  modalDescription?: string
  modalIllustration?: string
  modalLabel?: string
  modalPlaceholder?: string
  modalTitle?: string
  modalAlert?: string
  resourceFields?: ResourceField[]
  showEditModal?: boolean
  showDeleteModal?: boolean
  showModal?: boolean
  isLoading?: boolean
  afterChangeCallback?: () => void
  hideCustomResourceAssignment?: boolean
  title?: string
  showAllowedFieldTypes?: boolean
  showRequired?: boolean
  hideDragAndDropIcon?: boolean
  deleteTitle?: string
  deleteDescription?: string
  usePositionForSorting?: boolean
  trackingProperties?: TrackingProperties
}

interface CustomFieldWithPosition {
  field: CustomField | Field
  position: number
  assigned: boolean
}

const CustomFields = (props: React.PropsWithChildren<Props>) => {
  const paramsProcessor = OptionalParamsProcessor(props)
  const [openedModal, setOpenedModal] = React.useState<ModalStates>('none')
  const [currentField, setCurrentField] = React.useState<Field | CustomField | null>(null)
  const { has_custom_fields } = useFeatures(['custom_fields'])

  const {
    createCustomField,
    createResourceField,
    updateCustomField,
    destroyCustomField,
    customFields,
    deleteTitle,
    deleteDescription,
    resourceFields = [],
    canCreateCustomField = has_custom_fields,
    canEditCustomField = has_custom_fields,
    canDeleteCustomField = false,
    isLoading = false,
    hideCustomResourceAssignment = false,
    usePositionForSorting = false,
    trackingProperties,
    children,
  } = props

  const findResource = (fieldId: string) =>
    resourceFields.find((f) => f.attributes.field_id === fieldId)

  const runAfterChangeCallback = async () => {
    if (props.afterChangeCallback) {
      await props.afterChangeCallback()
    }
  }

  const nextPosition = () => {
    const resourceField = maxBy(resourceFields, (field) => field.attributes.position)

    return resourceField?.attributes.position + 1 || 0
  }

  const addNewQuestion = async (data: Record<string, unknown>) => {
    const { id } = await createCustomField({
      position: usePositionForSorting ? nextPosition() : null,
      ...data,
    })

    await assignToResource(id)()
  }

  const updateQuestion = (data: Record<string, unknown>) => {
    if (!currentField) return

    if (updateCustomField) {
      return updateCustomField(currentField, { ...data })
    }

    return currentField.save({ ...data })
  }

  const createQuestionFromDuplicate = async (data: Record<string, unknown>) => {
    if (!currentField || !canCreateCustomField) return

    await addNewQuestion(data)
  }

  const deleteQuestion = async () => {
    if (!currentField) return

    const { success } = await asyncAction(() =>
      destroyCustomField ? destroyCustomField(currentField) : currentField.destroy()
    )

    if (success) setOpenedModal('none')
  }

  const updateFieldsPosition = (items: CustomFieldWithPosition[]) => {
    const promises = compact(
      items.map((item, key) => {
        const resource = findResource(item.field.attributes.id)

        if (resource) {
          return resource.save({
            position: key,
          })
        }
      })
    )
    Promise.all(promises)
  }

  const assignToResource = (fieldId: string) => {
    return () => {
      if (!createResourceField) return

      const resourceField = findResource(fieldId)

      if (resourceField) {
        return asyncAction(async () => {
          await resourceField.destroy()
          await runAfterChangeCallback()
        })
      }

      return asyncAction(async () => {
        await createResourceField({
          field_id: fieldId,
          position: resourceFields.length + 1,
        })
        await runAfterChangeCallback()
      })
    }
  }

  const sortFields = (): CustomFieldWithPosition[] => {
    const isAssigned = (fieldId: string) => !!findResource(fieldId)

    const getPositionInResource = (fieldId: string) => {
      const resource = findResource(fieldId)

      if (resource) return resource.attributes.position
    }

    const results = customFields.map<CustomFieldWithPosition>((field) => {
      const position = field.has('id') ? getPositionInResource(field.attributes.id) : null

      const assigned = field.has('id') ? isAssigned(field.attributes.id) : false

      return {
        field,
        position,
        assigned,
      }
    })

    return sortBy(results, (item) => item.position)
  }

  const items = sortFields().filter((item) => item.field.has('id'))

  const RenderedItem = ({ item }: { item: CustomFieldWithPosition }) => {
    const { field, assigned } = item

    const editActions = {
      onClick: () => {
        setCurrentField(field)
        setOpenedModal('edit')
      },
      title: i18n.t('custom_fields.actions.edit'),
    }

    const duplicateActions = {
      onClick: () => {
        setCurrentField(field)
        setOpenedModal('duplicate')
      },
      title: i18n.t('custom_fields.actions.duplicate_question'),
    }

    const deleteActions = {
      onClick: () => {
        setCurrentField(field)
        setOpenedModal('delete')
      },
      type: 'wrong',
      title: i18n.t('custom_fields.actions.delete.remove_question'),
    }

    const actionsOverField = (() => {
      const actions = []

      if (canCreateCustomField) {
        actions.push(duplicateActions)
      }

      if (canEditCustomField) {
        actions.push(editActions)
      }

      if (canDeleteCustomField) {
        actions.push(deleteActions)
      }

      return actions
    })()

    return (
      <Item
        key={field.attributes.id}
        field={field}
        actions={actionsOverField}
        canAssignToResource={!!createResourceField && !hideCustomResourceAssignment}
        assignToResource={assignToResource(field.attributes.id)}
        assigned={assigned}
        hideDragAndDropIcon={props.hideDragAndDropIcon}
      />
    )
  }

  return (
    <>
      <Header
        canCreateCustomField={canCreateCustomField}
        paramsProcessor={paramsProcessor}
        toggleModal={() => setOpenedModal('new')}
        descriptionLink={props.descriptionLink}
      >
        {isLoading && <Loading />}
        {!isLoading && (
          <SortableBoxList
            items={items}
            onSortEnd={updateFieldsPosition}
            emptyMessage={paramsProcessor.get('emptyMessage', undefined)}
            itemAsKey={(item) => item.field.attributes.id}
            renderItem={(item) => <RenderedItem item={item} />}
          >
            {children}
          </SortableBoxList>
        )}
      </Header>

      {openedModal === 'new' && (
        <NewModal
          paramsProcessor={paramsProcessor}
          allowedFieldTypes={props.allowedFieldTypes}
          allowPermissions={props.allowPermissions}
          onClose={() => setOpenedModal('none')}
          onSubmit={(data: Record<string, unknown>) => addNewQuestion(data)}
          showAllowedFieldTypes={props.showAllowedFieldTypes}
          showRequired={props.showRequired}
          trackingProperties={trackingProperties}
        />
      )}

      {openedModal === 'edit' && (
        <EditModal
          allowedFieldTypes={props.allowedFieldTypes}
          allowPermissions={props.allowPermissions}
          showAllowedFieldTypes={props.showAllowedFieldTypes}
          showRequired={props.showRequired}
          field={currentField}
          onClose={() => setOpenedModal('none')}
          onSubmit={(data: Record<string, unknown>) => updateQuestion(data)}
          paramsProcessor={paramsProcessor}
        />
      )}

      {openedModal === 'duplicate' && (
        <DuplicateModal
          allowedFieldTypes={props.allowedFieldTypes}
          allowPermissions={props.allowPermissions}
          showAllowedFieldTypes={props.showAllowedFieldTypes}
          showRequired={props.showRequired}
          field={currentField}
          onClose={() => setOpenedModal('none')}
          onSubmit={(data: Record<string, unknown>) => createQuestionFromDuplicate(data)}
        />
      )}

      {openedModal === 'delete' && (
        <DeleteModal
          field={currentField}
          title={deleteTitle}
          description={deleteDescription}
          onClose={() => setOpenedModal('none')}
          onSubmit={() => deleteQuestion()}
        />
      )}
    </>
  )
}

export default observer(CustomFields)
